<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { emojiUrlToUnicode } from '@@/bits/emoji'
import { __ } from '@@/bits/intl'
import OzDrawerStatic from '@@/library/v4/components/OzDrawerStatic.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import EmojiPicker from '@@/vuecomponents/EmojiPicker.vue'
import { flip, offset } from '@floating-ui/vue'
import type { Emoji as EmojiType } from '@padlet/arvo'
import { usePreferredDark } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const surfacePanelsStore = useSurfacePanelsStore()
const { emojiPickerContext } = storeToRefs(surfacePanelsStore)
const { hideEmojiPicker } = surfacePanelsStore

// #region Popover background
const surfaceStore = useSurfaceStore()
const { colorScheme } = storeToRefs(surfaceStore)
const preferredDark = usePreferredDark()
const isDark = computed(() => {
  if (emojiPickerContext.value?.isInExpandedView) {
    return preferredDark.value
  }
  return colorScheme.value === 'dark'
})
// #endregion

const { isContainerSmallerThanTabletPortrait, isLandscapePhone } = storeToRefs(useSurfaceContainerSizeStore())

const xDrawer = computed(() => {
  return isLandscapePhone.value || isContainerSmallerThanTabletPortrait.value
})

const variant = computed(() => {
  if (isLandscapePhone.value) {
    return 'tablet'
  }
  if (isContainerSmallerThanTabletPortrait.value) {
    return 'phone'
  }
  return 'desktop'
})

const handleEmojiPick = (emojiUrl: string) => {
  const unicode = emojiUrlToUnicode(emojiUrl)
  if (unicode && emojiPickerContext.value?.postCid) {
    emojiPickerContext.value.onEmojiSelection?.(unicode)
    trackEvent('Emoji Picker', `Reacted in ${emojiPickerContext.value.type}`, undefined, undefined, {
      trackingContext: 'SurfaceEmojiPicker',
    })
  }

  // Close the emoji picker after selection
  handleClose()
}

const handleClose = () => {
  hideEmojiPicker()
}

const emojiPicker = ref<InstanceType<typeof EmojiPicker> | null>(null)
function focusEmojiPickerSearchBar() {
  // Don't focus the search bar on mobile devices to avoid jarring keyboard popup
  if (xDrawer.value || device.mobile) return
  emojiPicker.value?.focusSearchBar()
}

const shouldAddToRecents = (emoji: EmojiType) => emoji.url !== emojiPickerContext.value?.selectedEmojiUrl
</script>

<template>
  <!-- Mobile/Landscape Phone: Use OzDrawerStatic -->
  <OzDrawerStatic
    v-if="xDrawer"
    data-testid="surfaceEmojiPickerDrawer"
    :dark-mode="preferredDark"
    :full-size="true"
    :class="device.app && 'rounded-t-2.5xl'"
  >
    <template #body>
      <EmojiPicker
        class="overflow-hidden bg-light-ui-100 dark:bg-dark-ui-100 h-full"
        :variant="variant"
        :x-recents-category="true"
        :x-close-button="true"
        :dark-mode="preferredDark"
        :columns="6"
        :default-empty-result-message="__('We couldn’t find any emojis matching your search.')"
        :single-row-layout-empty-result-message="__('Emoji box empty – search again?')"
        :selected-emoji-url="emojiPickerContext?.selectedEmojiUrl"
        :should-add-to-recents="shouldAddToRecents"
        :x-checkmark="false"
        @close="handleClose"
        @pick="handleEmojiPick"
      />
    </template>
  </OzDrawerStatic>

  <!-- Desktop: Use OzPopover anchored to the button -->
  <Transition v-else-if="emojiPickerContext?.anchorElement" appear @after-enter="focusEmojiPickerSearchBar">
    <OzPopover
      :anchor="emojiPickerContext.anchorElement"
      :config="{
        placement: emojiPickerContext.type === 'post' ? 'bottom-start' : 'bottom-end',
        middleware: [offset(2), flip()],
      }"
      :x-scrim="false"
      :should-fade-in="true"
      :radius="16"
      :dark-mode="isDark"
      :default-empty-result-message="__('We couldn’t find any emojis matching your search.')"
      :single-row-layout-empty-result-message="__('Emoji box empty – search again?')"
      :box-classes="'w-[336px] h-[377px] overflow-hidden rounded-xl shadow-elevation-3'"
      @scrim-click="handleClose"
      @scrim-esc="handleClose"
    >
      <EmojiPicker
        ref="emojiPicker"
        class="h-full"
        :variant="variant"
        :x-recents-category="true"
        :dark-mode="isDark"
        :icon-size="24"
        :columns="8"
        :selected-emoji-url="emojiPickerContext?.selectedEmojiUrl"
        :should-add-to-recents="shouldAddToRecents"
        :x-checkmark="false"
        @close="handleClose"
        @pick="handleEmojiPick"
      />
    </OzPopover>
  </Transition>
</template>
